import React from 'react';
import { Download, Eye, FileText, Loader2, FileCheck2, History, Paperclip, PenLine, ShieldAlert, X } from 'lucide-react';
import { Button } from '../ui/button';
import { IssuedVersions } from '../shared/IssuedVersions';
import { formatDate } from '../../lib/utils';

const GROUPS = [['exporter', 'Exporter documents — generated from the B/L'], ['carrier', 'Carrier documents'], ['government', 'Authorities & chambers'], ['inspection', 'Inspection & fumigation companies'], ['destination', 'Importer, insurer & destination']];
const STATUS = {
  ready: ['To generate', 'bg-blue-50 text-blue-700'], generated: ['Generated', 'bg-success-100 text-success-700'], received: ['Received', 'bg-success-100 text-success-700'],
  pending: ['Awaiting issuer', 'bg-amber-50 text-amber-800'], optional: ['Optional', 'bg-gray-100 text-gray-600'], blocked: ['Blocked', 'bg-red-50 text-red-700'], available: ['Available', 'bg-blue-50 text-blue-700'],
};

function Pill({ status }) {
  const s = STATUS[status] || [status, 'bg-gray-100 text-gray-600'];
  return <span className={'text-[11px] font-medium uppercase tracking-[0.04em] px-2 py-0.5 rounded-full whitespace-nowrap ' + s[1]}>{s[0]}</span>;
}

function Row({ r, archive, busy, openVersions, toggleVersions, onPdf, onPreview, onRecord, onFile, onClear, onArchived, onCarrier }) {
  const versions = archive.filter(function (v) { return v.type === 'cargo-' + r.key; });
  const ext = r.external;
  return (
    <li className="py-3" data-testid={'cargo-req-' + r.key}>
      <div className="flex items-start gap-3">
        <div className={'w-8 h-8 rounded-sm flex items-center justify-center shrink-0 mt-0.5 ' + (r.status === 'blocked' ? 'bg-red-50 text-red-600' : r.generated ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-500')}>
          {r.status === 'blocked' ? <ShieldAlert className="h-4 w-4" /> : r.generated ? <FileText className="h-4 w-4" /> : <FileCheck2 className="h-4 w-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-medium text-ink text-[15px]">{r.title}</p>
            <span data-testid={'cargo-req-status-' + r.key}><Pill status={r.status} /></span>
            {r.latest_version && <span className="sv-mono text-[11px] bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded" data-testid={'cargo-req-version-' + r.key}>v{r.latest_version.version}</span>}
          </div>
          <p className="text-[13px] text-gray-600">{r.issued_by} · {r.description}</p>
          {ext && (
            <p className="text-[13px] text-ink mt-1 flex flex-wrap items-center gap-x-3" data-testid={'cargo-req-external-' + r.key}>
              {ext.reference && <span className="sv-mono">{ext.reference}</span>}
              {ext.issued_at && <span className="text-gray-600">issued {formatDate(ext.issued_at)}</span>}
              {ext.filename && <button type="button" onClick={function () { onFile(r); }} className="sv-link inline-flex items-center gap-1" data-testid={'cargo-req-file-' + r.key}><Paperclip className="h-3.5 w-3.5" /> {ext.filename}</button>}
            </p>
          )}
          {versions.length > 0 && (
            <button type="button" onClick={function () { toggleVersions(r.key); }} className="text-xs text-blue-700 hover:underline flex items-center gap-1 mt-1" data-testid={'cargo-req-versions-toggle-' + r.key}><History className="h-3.5 w-3.5" /> {openVersions[r.key] ? 'Hide' : 'Show'} issued versions ({versions.length})</button>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {r.generated && onPreview && <Button size="sm" variant="outline" disabled={busy !== ''} className="rounded-sm" title="Preview pages" onClick={function () { onPreview(r); }} data-testid={'cargo-req-preview-' + r.key}><Eye className="h-4 w-4" /></Button>}
          {r.generated && <Button size="sm" disabled={busy !== ''} className="rounded-sm bg-ink hover:bg-ink-800" onClick={function () { onPdf(r); }} data-testid={'cargo-req-pdf-' + r.key}>{busy === 'pdf-' + r.key ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-1.5" /> PDF</>}</Button>}
          {r.carrier_doc && <Button size="sm" variant="outline" className="rounded-sm" onClick={onCarrier} data-testid={'cargo-req-carrier-' + r.key}>Carrier tab</Button>}
          {!r.generated && !r.carrier_doc && r.status !== 'blocked' && <Button size="sm" variant="outline" className="rounded-sm" onClick={function () { onRecord(r); }} data-testid={'cargo-req-record-' + r.key}><PenLine className="h-4 w-4 mr-1.5" /> {ext ? 'Update' : 'Record'}</Button>}
          {ext && <button type="button" onClick={function () { onClear(r); }} className="h-8 w-8 grid place-items-center rounded text-gray-400 hover:text-red-600 hover:bg-red-50" title="Clear" data-testid={'cargo-req-clear-' + r.key}><X className="h-4 w-4" /></button>}
        </div>
      </div>
      {openVersions[r.key] && <IssuedVersions versions={versions} docType={'cargo-' + r.key} busyId={busy.startsWith('arch-') ? busy.slice(5) : ''} onDownload={onArchived} />}
    </li>
  );
}

// The full document set for the lane, grouped by who issues each document.
export function RequirementList(props) {
  const { requirements } = props;
  return (
    <div className="space-y-6" data-testid="cargo-requirements">
      {GROUPS.map(function (g) {
        const items = requirements.filter(function (r) { return r.issuer === g[0]; });
        if (!items.length) return null;
        return (
          <section key={g[0]} data-testid={'cargo-group-' + g[0]}>
            <h4 className="text-xs uppercase tracking-[0.08em] text-gray-500 mb-1">{g[1]} <span className="text-gray-400">· {items.length}</span></h4>
            <ul className="divide-y divide-gray-200">{items.map(function (r) { return <Row key={r.key} r={r} {...props} />; })}</ul>
          </section>
        );
      })}
    </div>
  );
}
